// What a job's outputs show as, shared by the job page and the studio's generation rows (an
// output's kind is `platform/media/output-kind`'s).
import type { GenerationOutput, Job } from "../../../api/generated/model";
import type { Medium } from "../../../platform/studio/draft";

/** Alt text from the prompt, cut to a sentence's length; a cleared prompt names the output. */
export function outputAlt(job: Job, output: GenerationOutput): string {
  const prompt = job.params?.prompt?.trim() ?? "";
  if (prompt === "") return `Output ${output.index + 1} of this job`;
  return prompt.length > 150 ? `${prompt.slice(0, 149)}…` : prompt;
}

/** Image jobs that make no picture for the Studio's rows: an analysis reads one (its output is
 *  JSON, G8-ad-copy) and an actor's portraits are the AI Actors page's. Live's My Generations
 *  (its creative generations) lists neither. Since issue 529 the server leaves them out of an
 *  unfiltered list (their contracts' `listed_in_studio`), so here they matter for the jobs that
 *  reach a page some other way: a clone link (`?clone=`), or a list filtered by type. */
const NOT_THE_STUDIOS: ReadonlySet<Job["type"]> = new Set(["image.analysis", "image.actor"]);

/**
 * The Studio page a job belongs on: its type's medium, the name's first part ("image.tweak",
 * "video.text"). A sound ("audio.tts"), a voice ("voice.design") or a text ("text.ad_copy") is
 * not a Studio row, so null: before issue 421 sounds fell to the Image page. Neither is an
 * analysis or an actor (`NOT_THE_STUDIOS`).
 */
export function jobMedium(job: Job): Medium | null {
  if (NOT_THE_STUDIOS.has(job.type)) return null;
  const medium = job.type.split(".")[0];
  return medium === "image" || medium === "video" ? medium : null;
}

/**
 * How a job's tile shows before its output is there, or after it failed: a video type's ("video.text",
 * "video.template", a talking head's) as live's video card, with its glyph and its blurred running
 * look, any other as a picture's (spec ui-perf/ui-switch-classic-simple-pro, decision 20).
 */
export function outputMedium(job: Job): Medium {
  return job.type.startsWith("video.") ? "video" : "image";
}
